import { useCallback, useEffect, useState } from "react";
import { Rocket, Zap, CalendarClock, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fmtDate } from "@/lib/api";
import { StatusBadge } from "./StatusBadge";
import { EmailFrame, mergeSubject } from "./emailPreview";

export function CampaignsPanel({ slot, project, onChange }) {
  const [templates, setTemplates] = useState([]);
  const [camps, setCamps] = useState([]);
  const [f, setF] = useState({ name: "", template_id: "", audience: "unsent" });
  const [when, setWhen] = useState("now");
  const [at, setAt] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/projects/${slot}/campaigns`).then((r) => setCamps(r.data)), [slot]);
  useEffect(() => { api.get(`/projects/${slot}/templates`).then((r) => setTemplates(r.data)); load(); }, [slot, load]);

  const running = camps.some((c) => c.status === "running");
  const hasScheduled = camps.some((c) => c.status === "scheduled");
  useEffect(() => {
    if (!running && !hasScheduled) return;
    const t = setInterval(() => { load(); onChange(); }, running ? 2500 : 30000);
    return () => clearInterval(t);
  }, [running, hasScheduled, load, onChange]);

  const selected = templates.find((t) => t.id === f.template_id);
  const count = f.audience === "unsent" ? project.stats.unsent : project.stats.total_leads - project.stats.unsubscribed;
  const later = when === "later";
  const atValid = later && at && new Date(at) > new Date();

  const launch = async () => {
    const msg = later ? `Schedule "${f.name}" for ${new Date(at).toLocaleString()}?` : `Send "${f.name}" to ${count} lead(s) now?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.post(`/projects/${slot}/campaigns`, { ...f, scheduled_at: later ? new Date(at).toISOString() : null });
      toast.success(later ? "Campaign scheduled" : "Campaign launched");
      setF({ ...f, name: "" });
      setAt("");
      load();
      onChange();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const cancel = async (c) => {
    if (!window.confirm(`Cancel scheduled campaign "${c.name}"?`)) return;
    try {
      await api.post(`/projects/${slot}/campaigns/${c.id}/cancel`);
      toast.success("Scheduled campaign cancelled");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-6" data-testid="campaigns-panel">
      <section className="rounded-xl border border-border bg-card p-6 space-y-5 h-fit">
        <h3 className="font-display text-lg font-semibold">New campaign</h3>
        <div className="space-y-1.5"><Label>Campaign name</Label>
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="campaign-name-input" /></div>
        <div className="space-y-1.5"><Label>Template</Label>
          <Select value={f.template_id} onValueChange={(v) => setF({ ...f, template_id: v })}>
            <SelectTrigger data-testid="campaign-template-select"><SelectValue placeholder="Choose template" /></SelectTrigger>
            <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
          {templates.length === 0 && <p className="text-xs text-amber-700">Create a template in the Templates tab first.</p>}
        </div>
        {selected && (
          <div className="space-y-1.5" data-testid="campaign-template-preview">
            <p className="text-xs text-muted-foreground truncate">Subject: <span className="text-foreground">{mergeSubject(selected.subject)}</span></p>
            <EmailFrame body={selected.body} format={selected.format || "text"} logoUrl={project.logo_url} height={220} testId="campaign-template-preview-frame" />
          </div>
        )}
        <div className="space-y-1.5"><Label>Recipients</Label>
          <Select value={f.audience} onValueChange={(v) => setF({ ...f, audience: v })}>
            <SelectTrigger data-testid="campaign-audience-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unsent">Leads not yet emailed</SelectItem>
              <SelectItem value="all">All leads (excl. unsubscribed)</SelectItem>
            </SelectContent>
          </Select></div>
        <p className="text-sm text-muted-foreground" data-testid="campaign-recipient-count"><span className="font-mono text-foreground">{count}</span> recipients</p>
        <div className="space-y-2"><Label>When to send</Label>
          <div className="grid grid-cols-2 rounded-lg border border-border p-0.5 bg-background">
            {[["now", "Send now", Zap], ["later", "Schedule", CalendarClock]].map(([v, label, Icon]) => (
              <button key={v} type="button" onClick={() => setWhen(v)} data-testid={`campaign-when-${v}`}
                className={`text-xs py-2 rounded-md flex items-center justify-center gap-1.5 transition-colors ${when === v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                <Icon className="h-3.5 w-3.5" />{label}
              </button>
            ))}
          </div>
          {later && (
            <div className="space-y-1">
              <Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} data-testid="campaign-schedule-input" />
              <p className="text-[11px] text-muted-foreground">Your local time. Sent within 15 minutes of the chosen time. Recipients are picked at send time.</p>
            </div>
          )}
        </div>
        {!project.from_email && <p className="text-xs text-amber-700">Set a From email in Settings before sending.</p>}
        <Button className="w-full" onClick={launch} disabled={busy || !f.name || !f.template_id || !count || !project.from_email || (later && !atValid)} data-testid="campaign-launch-button">
          {later ? <CalendarClock className="h-4 w-4 mr-2" /> : <Rocket className="h-4 w-4 mr-2" />}
          {busy ? "Saving…" : later ? "Schedule campaign" : "Launch campaign"}
        </Button>
      </section>
      <section className="space-y-3">
        {camps.length === 0 && <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground text-sm" data-testid="campaigns-empty">No campaigns yet.</div>}
        {camps.map((c) => (
          <div key={c.id} className="rounded-xl border border-border bg-card p-5" data-testid={`campaign-card-${c.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-display font-semibold truncate">{c.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{c.template_name} · {fmtDate(c.created_at)} · by {c.created_by}</p>
                {c.scheduled_at && (
                  <p className="text-xs text-indigo-700 mt-1 flex items-center gap-1" data-testid={`campaign-scheduled-at-${c.id}`}>
                    <CalendarClock className="h-3.5 w-3.5" />Scheduled for {fmtDate(c.scheduled_at)}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {c.status === "scheduled" && (
                  <Button size="sm" variant="ghost" className="text-rose-700 h-7" onClick={() => cancel(c)} data-testid={`campaign-cancel-${c.id}`}>
                    <XCircle className="h-3.5 w-3.5 mr-1" />Cancel
                  </Button>
                )}
                <StatusBadge status={c.status} testId={`campaign-status-${c.id}`} />
              </div>
            </div>
            <Progress value={((c.sent + c.failed) / c.total) * 100} className="h-1.5 mt-4" />
            <p className="text-xs font-mono mt-2 text-muted-foreground">
              <span className="text-emerald-700">{c.sent} sent</span> · <span className="text-rose-700">{c.failed} failed</span> · {c.total} total
            </p>
            {c.sent > 0 && (
              <div className="grid grid-cols-4 gap-2 mt-3" data-testid={`campaign-tracking-${c.id}`}>
                {[["delivered", "Delivered"], ["opened", "Opened"], ["clicked", "Clicked"], ["bounced", "Bounced"]].map(([k, l]) => (
                  <div key={k} className="rounded-md bg-background border border-border px-2.5 py-1.5">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p>
                    <p className="font-mono text-sm">{c[k] || 0} <span className="text-muted-foreground text-[11px]">{Math.round(((c[k] || 0) / c.sent) * 100)}%</span></p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}
